import * as React from 'react'
import { useListContext } from 'react-admin'
import { client } from '../../providers/data/client'

export const COUPON_ELIGIBLE_STATUSES = ['testComplete', 'manualReview', 'notifyComplete', 'error']

/**
 * Supplies the Kits list with the pre-flight result for every coupon-eligible row, so
 * CreateCouponButton can be coloured blue (no warnings) or grey (at least one warning)
 * before the admin clicks anything.
 *
 */
const CouponPreflightContext = React.createContext({ preflight: {}, loading: false })

export const useCouponPreflight = (kitId) => {
  const { preflight, loading } = React.useContext(CouponPreflightContext)
  return { entry: preflight[kitId], loading }
}

const CouponPreflightProvider = ({ children }) => {
  const { data, isLoading: listLoading } = useListContext()
  const [preflight, setPreflight] = React.useState({})
  const [loading, setLoading] = React.useState(false)

  const kitIdKey = React.useMemo(() => {
    if (listLoading || !data) {
      return ''
    }
    return data
      .filter((record) => record && COUPON_ELIGIBLE_STATUSES.indexOf(record.status) > -1 && !record.replacementCouponSentAt)
      .map((record) => record.kitId)
      .join(',')
  }, [data, listLoading])

  React.useEffect(() => {
    if (!kitIdKey) {
      setPreflight({})
      return undefined
    }

    let current = true
    setLoading(true)

    client()
      .get(`/coupons/preflight?kitIds=${kitIdKey}`)
      .then((res) => {
        if (current) {
          setPreflight(res.data || {})
        }
      })
      .catch((err) => {
        console.error('Could not load coupon pre-flight for this page:', err)
        if (current) {
          setPreflight({})
        }
      })
      .finally(() => {
        if (current) {
          setLoading(false)
        }
      })

    return () => {
      current = false
    }
  }, [kitIdKey])

  return <CouponPreflightContext.Provider value={{ preflight, loading }}>{children}</CouponPreflightContext.Provider>
}

export default CouponPreflightProvider
